import {
    Directive,
    ElementRef,
    Input,
    Output,
    EventEmitter,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    HostListener,
} from '@angular/core';
import { Chart, ChartData, ChartOptions as ChartJsOptions } from 'chart.js';
import { mergeDeep, deepEqual, safeClone } from '@client/app/shared/utils/object-utils';
import { ChartJsProvider } from '../providers/charts.provider';
import {
    ChartType,
    ChartOptionsV2,
    adaptChartType,
    transformOptionsV2ToV3,
    ChartDefaults,
} from '../adapters';

@Directive({
    selector: '[chartBase]',
})
export class ChartBaseDirective implements OnChanges, OnDestroy {
    @Input() chartType?: ChartType | undefined;
    @Input() chartData?: any[] | any[][] | undefined;
    @Input() chartLabels?: string[] | undefined;
    @Input() chartOptions?: ChartOptionsV2 | undefined;
    @Input() chartSeries?: string[] | undefined;
    @Input() chartColors?: any[] | undefined;
    @Input() chartGetColor?: (() => any) | undefined;
    @Input() chartDatasetOverride?: Array<Record<string, unknown>> | Record<string, unknown> | undefined;

    @Output() chartClick = new EventEmitter<{ points: any[], event: MouseEvent, point: any }>();
    @Output() chartHover = new EventEmitter<{ points: any[], event: MouseEvent, point: any }>();

    private chart: Chart | undefined;
    private readonly useExcanvas: boolean;
    private readonly lastHoverState = {
        point: undefined as any,
        points: [] as any[],
    };

    constructor (
        private readonly elementRef: ElementRef<HTMLCanvasElement>,
        private readonly chartJsProvider: ChartJsProvider,
    ) {
        this.useExcanvas = typeof (globalThis as any).G_vmlCanvasManager === 'object' &&
      (globalThis as any).G_vmlCanvasManager !== null &&
      typeof (globalThis as any).G_vmlCanvasManager.initElement === 'function';

        if (this.useExcanvas) {
            (globalThis as any).G_vmlCanvasManager.initElement(this.elementRef.nativeElement);
        }
    }

    ngOnChanges (changes: SimpleChanges): void {
        const dataChanged = changes['chartData'] && this.isDataArrayChanged(changes.chartData.previousValue, changes.chartData.currentValue);
        const otherChanged =
            changes['chartType'] ||
            changes['chartSeries'] ||
            (changes['chartLabels'] && this.isStringArrayChanged(changes.chartLabels.previousValue, changes.chartLabels.currentValue)) ||
            changes['chartOptions'] ||
            (changes['chartColors'] && this.isStringArrayChanged(changes.chartColors.previousValue, changes.chartColors.currentValue)) ||
            changes['chartDatasetOverride'];

        if (dataChanged && !changes.chartData.firstChange && !otherChanged) {
            this.handleDataChange(changes['chartData'].currentValue, changes['chartData'].previousValue);
            return;
        }

        if (otherChanged) {
            this.handleOtherChange();
        }
    }

    ngOnDestroy (): void {
        this.destroyChart();
    }

    @HostListener('window:resize')
    onResize (): void {
        if (this.chart) {
            this.chart.resize();
        }
    }

    isStringArrayChanged (prev: string[], cur: string[]) {
        if (!prev || !cur || prev.length !== cur.length) {
            return true;
        }
        for (let i = 0; i < prev.length; i++) {
            if (prev[i] !== cur[i]) {
                return true;
            }
        }
        return false;
    }

    isDataArrayChanged (prev: any[], cur: any[]) {
        if (!prev || !cur || prev.length !== cur.length) {
            return true;
        }
        for (let i = 0; i < prev.length; i++) {
            if (Array.isArray(prev[i]) && Array.isArray(cur[i])) {
                if (prev[i].length !== cur[i].length || !deepEqual(prev[i], cur[i])) {
                    return true;
                }
            } else if (!deepEqual(prev[i], cur[i])) {
                return true;
            }
        }
        return false;

    }

    private handleDataChange (newVal: any, oldVal: any): void {
        if (!newVal?.length || (Array.isArray(newVal[0]) && !newVal[0]?.length)) {
            this.destroyChart();
            return;
        }

        const chartType = this.chartType;
        if (!chartType) {
            return;
        }

        if (this.chart && this.canUpdateChart(newVal, oldVal)) {
            this.updateChart(newVal);
        } else {
            this.createChart();
        }
    }

    private handleOtherChange (): void {
        const chartType = this.chartType;
        if (!chartType) {
            return;
        }

        // chart.update() doesn't work for series and labels
        // so we have to re-create the chart entirely
        this.createChart();
    }

    private createChart (): void {
        const type = this.chartType;
        if (!type) {
            return;
        }

        const options = this.getChartOptions(type);
        if (!this.hasData() || !this.canDisplay(options)) {
            return;
        }

        const canvas = this.elementRef.nativeElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Adapt v2 chart type to v3
        const v3Type = adaptChartType(type);
        const data = this.getChartData(type);

        // Destroy old chart if it exists to avoid ghost charts issue

        this.destroyChart();
        this.chart = new Chart(ctx, {
            type: v3Type,
            data: data,
            options: options,
        });

        this.bindEvents(canvas);
    }

    private canUpdateChart (newVal: any, oldVal: any): boolean {
        if (newVal && oldVal && newVal.length && oldVal.length) {
            if (Array.isArray(newVal[0])) {
                return newVal.length === oldVal.length && newVal.every((element: any[], index: number) => {
                    return element.length === oldVal[index].length;
                });
            }
            const oldSum = oldVal.reduce((sum: number, val: number) => sum + val, 0);
            if (oldSum > 0) {
                return newVal.length === oldVal.length;
            }
            return false;
        }
        return false;
    }

    private updateChart (values: any): void {
        if (!this.chart) {
            return;
        }

        if (Array.isArray(this.chartData?.[0])) {
            this.chart?.data?.datasets?.forEach((dataset, i) => {
                dataset.data = values[i];
            });
        } else if (this.chart?.data?.datasets?.[0]?.data) {
            this.chart.data.datasets[0].data = values;
        }

        this.chart.update();
    }

    private getChartOptions (type: ChartType): ChartJsOptions {
        // Get v2-style options from provider and merge with component options
        const v2Options = mergeDeep({}, this.chartJsProvider.getOptions(adaptChartType(type)), this.chartOptions || {}) as ChartOptionsV2;
        // Transform v2 options to v3 format
        return transformOptionsV2ToV3(v2Options, type);
    }

    private hasData (): boolean {
        return !!this.chartData?.length;
    }

    private canDisplay (options: any): boolean {
        const elem = this.elementRef.nativeElement;
        if (options.responsive && elem.clientHeight === 0) {
            setTimeout(() => {
                this.createChart();
            }, 50);
            return false;
        }
        return true;
    }

    private getChartData (type: ChartType): ChartData {
        const colors = this.getColors(type);
        return Array.isArray(this.chartData?.[0])
            ? this.getDataSets(this.chartLabels || [], this.chartData as any[][], this.chartSeries || [], colors, this.chartDatasetOverride)
            : this.getData(this.chartLabels || [], this.chartData || [], colors, this.chartDatasetOverride);
    }

    private getDataSets (labels: string[], data: any[][], series: string[], colors: any[], datasetOverride?: any): ChartData {
        return {
            labels: labels,
            datasets: data.map((item, i) => {
                const dataset = mergeDeep({}, colors[i], {
                    label: series[i],
                    data: item,
                });
                if (datasetOverride && datasetOverride.length >= i) {
                    mergeDeep(dataset, datasetOverride[i]);
                }
                return dataset as ChartData['datasets'][number];
            }),
        };
    }

    private getData (labels: string[], data: any[], colors: any[], datasetOverride?: any): ChartData {
        const dataset: ChartData = {
            labels: labels,
            datasets: [{
                data: data,
                backgroundColor: colors.map((color: any) => color.pointBackgroundColor),
                hoverBackgroundColor: colors.map((color: any) => color.backgroundColor),
            }],
        };
        if (datasetOverride) {
            if (dataset?.datasets?.[0]) {
                mergeDeep(dataset.datasets[0], datasetOverride);
            }
        }
        return dataset;
    }

    private getColors (type: ChartType): any[] {
        const defaultColors = ChartDefaults.colors;
        const colors = safeClone(this.chartColors ||
      this.chartJsProvider.getOptions(adaptChartType(type)).chartColors ||
      defaultColors,
        );

        const notEnoughColors = colors.length < (this.chartData?.length || 0);
        while (colors.length < (this.chartData?.length || 0)) {
            colors.push(this.getChartColorFn()());
        }

        // mutate colors in this case as we don't want
        // the colors to change on each refresh
        if (notEnoughColors) {
            this.chartColors = colors;
        }

        return colors.map(color => this.convertColor(color));
    }

    private getChartColorFn (): () => any {
        return typeof this.chartGetColor === 'function' ? this.chartGetColor : this.getRandomColor.bind(this);
    }

    private convertColor (color: any): any {
    // Allows RGB and RGBA colors to be input as a string: e.g.: "rgb(159,204,0)", "rgba(159,204,0, 0.5)"
        if (typeof color === 'string' && color.startsWith('r')) {
            return this.getColor(this.rgbStringToRgb(color));
        }
        // Allows hex colors to be input as a string.
        if (typeof color === 'string' && color.startsWith('#')) {
            return this.getColor(this.hexToRgb(color.slice(1)));
        }
        // Allows colors to be input as an object, bypassing getColor() entirely
        if (typeof color === 'object' && color !== null) {
            return color;
        }
        return this.getRandomColor();
    }

    private getRandomColor (): any {
        const color = [this.getRandomInt(0, 255), this.getRandomInt(0, 255), this.getRandomInt(0, 255)];
        return this.getColor(color);
    }

    private getColor (color: number[]): any {
        const alpha = color[3] || 1;
        color = color.slice(0, 3);
        return {
            backgroundColor: this.rgba(color, 0.2),
            pointBackgroundColor: this.rgba(color, alpha),
            pointHoverBackgroundColor: this.rgba(color, 0.8),
            borderColor: this.rgba(color, alpha),
            pointBorderColor: '#fff',
            pointHoverBorderColor: this.rgba(color, alpha),
        };
    }

    private getRandomInt (min: number, max: number): number {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    private rgba (color: number[], alpha: number): string {
    // rgba not supported by IE8
        return this.useExcanvas ? `rgb(${color.join(',')})` : `rgba(${color.concat(alpha).join(',')})`;
    }

    private hexToRgb (hex: string): number[] {
        const bigint = Number.parseInt(hex, 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b];
    }

    private rgbStringToRgb (color: string): number[] {
        const regex = /^rgba?\(([\d,.]+)\)$/;
        const match = regex.exec(color);
        if (!match) {
            throw new Error('Cannot parse rgb value');
        }
        const colorArray = match[1].split(',');
        return colorArray.map(Number);
    }

    private bindEvents (canvas: HTMLCanvasElement): void {
        canvas.onclick = this.getEventHandler('click', false);
        canvas.onmousemove = this.getEventHandler('hover', true);
    }

    private getEventHandler (action: 'click' | 'hover', triggerOnlyOnChange: boolean): (evt: MouseEvent) => void {
        return (evt: MouseEvent) => {
            if (!this.chart) {
                return;
            }

            const atEvent = (this.chart as any).getElementAtEvent || (this.chart as any).getPointAtEvent;
            const atEvents = (this.chart as any).getElementsAtEvent || (this.chart as any).getPointsAtEvent;

            if (atEvents) {
                const points = atEvents.call(this.chart, evt);
                const point = atEvent ? atEvent.call(this.chart, evt)[0] : undefined;

                if (!triggerOnlyOnChange ||
          (!deepEqual(this.lastHoverState.points, points) && !deepEqual(this.lastHoverState.point, point))
                ) {
                    this.lastHoverState.point = point;
                    this.lastHoverState.points = points;

                    setTimeout(() => {
                        if (action === 'click') {
                            this.chartClick.emit({ points, event: evt, point });
                        } else {
                            this.chartHover.emit({ points, event: evt, point });
                        }
                    });
                }
            }
        };
    }

    private destroyChart (): void {
        if (!this.chart) {
            return;
        }
        this.chart.destroy();
        this.chart = undefined;
    }

    private isEmpty (value: any): boolean {
        return !value ||
      (Array.isArray(value) && !value.length) ||
      (typeof value === 'object' && !Object.keys(value).length);
    }
}
